// Pastilla de estado del TPV: sustituye a las bandas rojas/amarillas a todo
// lo ancho (tiempo real, worker de impresión, impresora). SOLO AVISA:
//  - Nada si todo va bien.
//  - Si algo falla: punto rojo/ámbar + texto corto del problema más grave
//    («Sin tiempo real · 12 s», «Impresión sin conexión», «Impresora: sin
//    papel»). En móvil, tablet y pantallas pequeñas (< lg) solo el punto:
//    el texto desplazaba los iconos de la barra del TPV y de la carta.
//  - Al pulsarla, un Popover con cada subsistema y sus acciones.
// Nunca tapa contenido ni desplaza el layout: va dentro de la barra
// (TopBar / cabecera de la carta) o, como último recurso, flotante y
// pequeña en una esquina (solo la propia pastilla recibe clics).
//
// variant:
//   - "topbar":   en el TopBar del DashboardLayout.
//   - "embedded": dentro de una cabecera de la carta; oculta la flotante.
//   - "floating": la monta TpvCartaLayout para las pantallas sin cabecera.
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Box, Button, ButtonBase, Divider, Popover, Stack, Typography } from "@mui/material";
import { PATH_APP } from "src/routes/paths";
import { dismissPrinterIssues } from "src/redux/slices/workerStatus";
import WorkerOfflineHelpDialog from "src/layouts/DashboardLayout/WorkerOfflineHelpDialog";
import PrinterIssuesHelpDialog from "src/layouts/DashboardLayout/PrinterIssuesHelpDialog";
import { useIsTpvPreview } from "src/utils/tpvLayout/PreviewContext";
import { NOTICE_TEXT } from "./staleStatus";
import {
  getEmbeddedPillCount,
  getTpvStatus,
  registerEmbeddedPill,
  retryRealtime,
  setWorkerStatusInput,
  startTpvStatus,
  subscribeEmbeddedPills,
  subscribeTpvStatus,
} from "./tpvStatusStore";

const DOT_COLOR = { error: "error.main", warning: "warning.main" };

/** Estado compartido de los tres subsistemas (arranca el sondeo mientras haya pastillas). */
export function useTpvStatus() {
  const workerStatus = useSelector((state) => state.workerStatus);
  const [status, setStatus] = useState(getTpvStatus);

  useEffect(() => {
    setWorkerStatusInput(workerStatus);
  }, [workerStatus]);

  useEffect(() => {
    const off = subscribeTpvStatus(setStatus);
    const stop = startTpvStatus();
    setStatus(getTpvStatus());
    return () => {
      off();
      stop();
    };
  }, []);

  return status;
}

function Dot({ severity, size = 10 }) {
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: DOT_COLOR[severity] || "success.main",
      }}
    />
  );
}

function Row({ title, severity, state, detail, children }) {
  return (
    <Box sx={{ py: 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Dot severity={severity} size={8} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: severity ? DOT_COLOR[severity] : "success.main" }}>
          {state}
        </Typography>
      </Stack>
      {detail && (
        <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.5, pl: 2 }}>
          {detail}
        </Typography>
      )}
      {children && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, pl: 2, flexWrap: "wrap" }}>
          {children}
        </Stack>
      )}
    </Box>
  );
}

const actionSx = { textTransform: "none", fontWeight: 700, py: 0.25, minHeight: 30 };

function TpvStatusPillInner({ variant }) {
  const status = useTpvStatus();
  const history = useHistory();
  const dispatch = useDispatch();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [help, setHelp] = useState(null); // "worker" | "printer"
  const [embeddedCount, setEmbeddedCount] = useState(getEmbeddedPillCount);

  useEffect(() => {
    if (variant === "embedded") return registerEmbeddedPill();
    if (variant !== "floating") return undefined;
    setEmbeddedCount(getEmbeddedPillCount());
    return subscribeEmbeddedPills(setEmbeddedCount);
  }, [variant]);

  const { items, worst, printerFingerprint } = status;
  const byKey = Object.fromEntries(items.map((it) => [it.key, it]));
  const hidden = !worst || (variant === "floating" && embeddedCount > 0);

  // Si el problema se resuelve con el Popover abierto, se cierra solo.
  useEffect(() => {
    if (hidden) setOpen(false);
  }, [hidden]);

  const helpDialogs = (
    <>
      <WorkerOfflineHelpDialog open={help === "worker"} onClose={() => setHelp(null)} />
      <PrinterIssuesHelpDialog open={help === "printer"} onClose={() => setHelp(null)} />
    </>
  );
  if (hidden) return help ? helpDialogs : null;

  const go = (path) => {
    setOpen(false);
    history.push(path);
  };
  const realtime = byKey.realtime;
  const worker = byKey.worker;
  const printer = byKey.printer;
  const floating = variant === "floating";

  const pill = (
    <ButtonBase
      ref={anchorRef}
      onClick={() => setOpen(true)}
      data-testid="tpv-status-pill"
      aria-label={`Estado del TPV: ${worst.short}`}
      aria-haspopup="dialog"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        minWidth: 32,
        minHeight: 32,
        px: { xs: 1, lg: 1.25 },
        borderRadius: 16,
        flexShrink: 0,
        maxWidth: { lg: 240 },
        border: 1,
        borderColor: worst.severity === "error" ? "error.light" : "warning.light",
        bgcolor: "background.paper",
        boxShadow: floating ? 3 : 0,
        color: "text.primary",
        pointerEvents: "auto",
      }}
    >
      <Dot severity={worst.severity} />
      <Box
        component="span"
        sx={{
          display: { xs: "none", lg: "inline" },
          fontSize: 12.5,
          fontWeight: 700,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {worst.short}
      </Box>
      {items.length > 1 && (
        <Box component="span" sx={{ display: { xs: "none", lg: "inline" }, fontSize: 11, color: "text.secondary" }}>
          {`+${items.length - 1}`}
        </Box>
      )}
    </ButtonBase>
  );

  return (
    <>
      {floating ? (
        <Box
          sx={{
            position: "fixed",
            top: "max(8px, env(safe-area-inset-top))",
            right: 8,
            zIndex: (theme) => theme.zIndex.appBar + 1,
            pointerEvents: "none",
          }}
        >
          {pill}
        </Box>
      ) : (
        pill
      )}

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 320, maxWidth: "calc(100vw - 32px)", px: 2, py: 0.5 } }}
      >
        <Row
          title="Tiempo real"
          severity={realtime?.severity}
          state={realtime ? realtime.short : "Conectado"}
          detail={realtime ? (realtime.variant === "offline" ? NOTICE_TEXT.offline : NOTICE_TEXT.realtime) : null}
        >
          {realtime && (
            <Button size="small" variant="contained" onClick={retryRealtime} sx={actionSx}>
              {NOTICE_TEXT.retry}
            </Button>
          )}
        </Row>
        <Divider />
        <Row
          title="Impresión (worker)"
          severity={worker?.severity}
          state={worker ? "Sin conexión" : "Correcto"}
          detail={worker ? `${worker.detail} — las comandas pueden no estar imprimiéndose.` : null}
        >
          {worker && (
            <>
              <Button size="small" variant="outlined" onClick={() => go(PATH_APP.tuCarta.printerSystemStatus)} sx={actionSx}>
                Ver estado
              </Button>
              <Button size="small" onClick={() => { setOpen(false); setHelp("worker"); }} sx={actionSx}>
                Ayuda
              </Button>
            </>
          )}
        </Row>
        <Divider />
        <Row
          title="Impresoras"
          severity={printer?.severity}
          state={printer ? "Con problemas" : "Correcto"}
          detail={printer?.detail}
        >
          {printer && (
            <>
              <Button size="small" variant="outlined" onClick={() => go(PATH_APP.tuCarta.printerSystemStatus)} sx={actionSx}>
                Ver estado
              </Button>
              <Button size="small" onClick={() => { setOpen(false); setHelp("printer"); }} sx={actionSx}>
                Ayuda
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => dispatch(dismissPrinterIssues(printerFingerprint))}
                sx={actionSx}
              >
                Ocultar aviso
              </Button>
            </>
          )}
        </Row>
      </Popover>
      {helpDialogs}
    </>
  );
}

export default function TpvStatusPill({ variant = "topbar" }) {
  // En la vista previa de «Apariencia TPV» (Ajustes) la carta va con un store
  // de demostración: ni pastilla ni sondeo (ya está la del TopBar real).
  const preview = useIsTpvPreview();
  if (preview) return null;
  return <TpvStatusPillInner variant={variant} />;
}

TpvStatusPillInner.propTypes = {
  variant: PropTypes.oneOf(["topbar", "embedded", "floating"]).isRequired,
};

TpvStatusPill.propTypes = {
  variant: PropTypes.oneOf(["topbar", "embedded", "floating"]),
};
